import { useState, useEffect, useRef, useCallback } from 'react'
import useFeatureFlag from '../../hooks/useFeatureFlag'
import { notification } from 'antd'
import {
  LookupQuery,
  LookupResult,
  createRating,
  createSession,
  deleteRating,
  fetchLookupOptions,
  restoreSession,
  sendDecision as apiSendDecision,
  sendGroupDecision as apiSendGroupDecision,
  sendMessage as apiSendMessage,
  submitForm as apiSubmitForm,
  submitQuestionSet as apiSubmitQuestionSet,
} from './aiAssistantApi'
import {
  SSEEvent,
  ChatMessage,
  appendPart,
  sseEventToPart,
  TextPart,
  shouldInitSession,
  loadSessionData,
} from './aiAssistantModel'
import {
  CHAT_RESTARTED_TEXT,
  chatErrorText,
  keepLastExchange,
  runWithSessionRecovery,
  shouldAdoptSession,
} from './sessionRecoveryModel'
import {
  RatingEffect,
  RatingState,
  initialRatingState,
  rate as rateRating,
  ratingCreated,
  submitRationale as submitRationaleModel,
} from './messageRatingModel'
import { useFetchChatSession } from './chatHistoryQueries'
import { convertSessionMessages } from '../AdminApp/Chats/chatSearchModel'
import { COMPACT_TOOL_CALLS_FEATURE } from './messageBubbleModel'

export type UseChatReturn = {
  messages: ChatMessage[]
  isStreaming: boolean
  error: string | null
  sessionId: string | null
  ratings: Map<string, RatingState>
  pendingRatings: Set<string>
  sendMessage: (text: string, stepOut?: boolean) => void
  sendDecision: (toolCallId: string, approved: boolean) => void
  sendGroupDecision: (groupId: string, approved: boolean) => void
  submitForm: (formId: string, values: Record<string, unknown>) => void
  submitQuestionSet: (questionSetId: string, answers: Record<string, unknown>) => void
  lookupOptions: (query: LookupQuery) => Promise<LookupResult>
  cancelStream: () => void
  resetSession: () => void
  loadSession: (sessionId: string) => Promise<void>
  rateMessage: (message: ChatMessage, thumbsUp: boolean) => void
  submitMessageRationale: (message: ChatMessage, text: string) => void
}

function extractResponseText(message: ChatMessage): string {
  return message.parts
    .filter((p): p is TextPart => p.type === 'text')
    .map((p) => p.content)
    .join('')
}

export function useChat(enabled: boolean): UseChatReturn {
  const compactToolCalls = useFeatureFlag(COMPACT_TOOL_CALLS_FEATURE)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [isStreaming, setIsStreaming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sessionId, setSessionId] = useState<string | null>(null)

  const [ratings, setRatings] = useState<Map<string, RatingState>>(new Map())
  const [pendingRatings, setPendingRatings] = useState<Set<string>>(new Set())

  const abortRef = useRef<AbortController | null>(null)
  const sessionIdRef = useRef<string | null>(null)
  const isStreamingRef = useRef(false)
  const ratingsRef = useRef<Map<string, RatingState>>(new Map())
  const pendingRatingsRef = useRef<Set<string>>(new Set())

  const fetchChatSession = useFetchChatSession()

  const writeRating = useCallback((messageId: string, next: RatingState) => {
    ratingsRef.current.set(messageId, next)
    setRatings(new Map(ratingsRef.current))
  }, [])

  const setPending = useCallback((messageId: string, pending: boolean) => {
    if (pending) {
      pendingRatingsRef.current.add(messageId)
    } else {
      pendingRatingsRef.current.delete(messageId)
    }
    setPendingRatings(new Set(pendingRatingsRef.current))
  }, [])

  const applyEffect = useCallback(
    async (message: ChatMessage, effect: RatingEffect, priorState: RatingState) => {
      const sid = sessionIdRef.current
      if (!sid || effect.type === 'none') {
        return
      }
      setPending(message.id, true)
      try {
        if (effect.type === 'create') {
          const ratingId = await createRating(sid, {
            response_text: extractResponseText(message),
            thumbs_up: effect.thumbsUp,
            rationale: effect.rationale,
          })
          if (sessionIdRef.current !== sid) {
            return
          }
          const current = ratingsRef.current.get(message.id) ?? initialRatingState
          writeRating(message.id, ratingCreated(current, ratingId))
        } else if (effect.type === 'delete') {
          await deleteRating(sid, effect.ratingId)
        } else if (effect.type === 'replace') {
          await deleteRating(sid, effect.deleteRatingId)
          const ratingId = await createRating(sid, {
            response_text: extractResponseText(message),
            thumbs_up: effect.thumbsUp,
            rationale: effect.rationale,
          })
          if (sessionIdRef.current !== sid) {
            return
          }
          const current = ratingsRef.current.get(message.id) ?? initialRatingState
          writeRating(message.id, ratingCreated(current, ratingId))
        }
      } catch {
        if (sessionIdRef.current === sid) {
          writeRating(message.id, priorState)
          notification.error({ message: 'Failed to update rating' })
        }
      } finally {
        setPending(message.id, false)
      }
    },
    [setPending, writeRating]
  )

  const rateMessage = useCallback(
    (message: ChatMessage, thumbsUp: boolean) => {
      if (pendingRatingsRef.current.has(message.id)) {
        return
      }
      const current = ratingsRef.current.get(message.id) ?? initialRatingState
      const { state, effect } = rateRating(current, thumbsUp)
      writeRating(message.id, state)
      applyEffect(message, effect, current)
    },
    [applyEffect, writeRating]
  )

  const submitMessageRationale = useCallback(
    (message: ChatMessage, text: string) => {
      if (pendingRatingsRef.current.has(message.id)) {
        return
      }
      const current = ratingsRef.current.get(message.id) ?? initialRatingState
      const { state, effect } = submitRationaleModel(current, text)
      writeRating(message.id, state)
      applyEffect(message, effect, current)
    },
    [applyEffect, writeRating]
  )

  const clearRatings = useCallback(() => {
    ratingsRef.current = new Map()
    setRatings(new Map())
    pendingRatingsRef.current = new Set()
    setPendingRatings(new Set())
  }, [])

  const initSession = useCallback(async () => {
    try {
      const id = await createSession()
      setSessionId(id)
      sessionIdRef.current = id
    } catch (err) {
      setError(chatErrorText(err))
    }
  }, [])

  useEffect(() => {
    if (shouldInitSession(enabled, sessionIdRef.current)) {
      initSession()
    }
    return () => {
      abortRef.current?.abort()
    }
  }, [enabled, initSession])

  const handleSSEEvent = useCallback((event: SSEEvent) => {
    if (event.event === 'done') {
      isStreamingRef.current = false
      setIsStreaming(false)
      return
    }

    setMessages((prev) => {
      const updated = [...prev]
      const last = updated[updated.length - 1]
      if (!last || last.role !== 'assistant') return prev

      const assistantMsg = { ...last, parts: [...last.parts] }
      updated[updated.length - 1] = assistantMsg

      if (event.event === 'text_chunk') {
        const lastPart = assistantMsg.parts[assistantMsg.parts.length - 1]
        if (lastPart && lastPart.type === 'text') {
          assistantMsg.parts[assistantMsg.parts.length - 1] = {
            ...lastPart,
            content: lastPart.content + event.content,
          }
          return updated
        }
      }

      // appendPart, not push: a re-sent group approval card replaces the one
      // already on screen instead of stacking a stale copy above it.
      assistantMsg.parts = appendPart(assistantMsg.parts, sseEventToPart(event))
      return updated
    })
  }, [])

  // Every streamed request goes through here: the streaming flags, cancel,
  // and recovery from a chat that expired while idle (chat#274). Only a send
  // may move to a new chat; an approval or form answer belongs to this one.
  const runStream = useCallback(
    async (
      request: (sessionId: string, signal: AbortSignal) => Promise<void>,
      canStartNew: boolean
    ) => {
      const sid = sessionIdRef.current
      if (!sid) {
        return
      }

      isStreamingRef.current = true
      setIsStreaming(true)
      setError(null)

      const controller = new AbortController()
      abortRef.current = controller

      // Move to the new chat as soon as it exists, so anything the retry
      // streams in (a form's lookups, a rating) already talks to it.
      const startNew = async () => {
        const id = await createSession()
        if (shouldAdoptSession(sid, id, sessionIdRef.current)) {
          sessionIdRef.current = id
          setSessionId(id)
          clearRatings()
          setMessages(keepLastExchange)
          notification.info({ message: CHAT_RESTARTED_TEXT })
        }
        return id
      }

      try {
        await runWithSessionRecovery(
          sid,
          (id) => request(id, controller.signal),
          restoreSession,
          canStartNew ? startNew : undefined,
          controller.signal
        )
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return
        }
        console.warn('AI assistant request failed', err)
        setError(chatErrorText(err))
      } finally {
        isStreamingRef.current = false
        setIsStreaming(false)
        abortRef.current = null
      }
    },
    [clearRatings]
  )

  const sendMessage = useCallback(
    async (text: string, stepOut?: boolean) => {
      if (!sessionIdRef.current || isStreamingRef.current) {
        return
      }

      const userMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'user',
        parts: [{ type: 'text', content: text }],
      }
      const assistantMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        parts: [],
      }

      // Added once: a retry after recovery streams into this same bubble.
      setMessages((prev) => [...prev, userMsg, assistantMsg])

      await runStream(
        (sid, signal) => apiSendMessage(sid, text, handleSSEEvent, signal, stepOut),
        true
      )
    },
    [handleSSEEvent, runStream]
  )

  const sendDecision = useCallback(
    async (toolCallId: string, approved: boolean) => {
      if (!sessionIdRef.current || isStreamingRef.current) {
        return
      }

      // No new bubble: the resumed tool_result and assistant reply stream into
      // the existing assistant message that holds the permission request.
      await runStream(
        (sid, signal) => apiSendDecision(sid, toolCallId, approved, handleSSEEvent, signal),
        false
      )
    },
    [handleSSEEvent, runStream]
  )

  const sendGroupDecision = useCallback(
    async (groupId: string, approved: boolean) => {
      if (!sessionIdRef.current || isStreamingRef.current) {
        return
      }

      // No new bubble, as with a single decision: the row results and any
      // assistant reply stream into the message already holding the card.
      await runStream(
        (sid, signal) => apiSendGroupDecision(sid, groupId, approved, handleSSEEvent, signal),
        false
      )
    },
    [handleSSEEvent, runStream]
  )

  const submitForm = useCallback(
    async (formId: string, values: Record<string, unknown>) => {
      if (!sessionIdRef.current || isStreamingRef.current) {
        return
      }

      // No new bubble: the resumed tool_call, tool_result, and assistant reply
      // stream into the existing assistant message that holds the form.
      await runStream(
        (sid, signal) => apiSubmitForm(sid, formId, values, handleSSEEvent, signal),
        false
      )
    },
    [handleSSEEvent, runStream]
  )

  const submitQuestionSet = useCallback(
    async (questionSetId: string, answers: Record<string, unknown>) => {
      if (!sessionIdRef.current || isStreamingRef.current) {
        return
      }

      // No new bubble: the resumed tool_result (the structured answers) and
      // assistant reply stream into the existing assistant message that holds
      // the question set.
      await runStream(
        (sid, signal) => apiSubmitQuestionSet(sid, questionSetId, answers, handleSSEEvent, signal),
        false
      )
    },
    [handleSSEEvent, runStream]
  )

  const lookupOptions = useCallback(
    (query: LookupQuery): Promise<LookupResult> => {
      const sid = sessionIdRef.current
      if (!sid) {
        return Promise.resolve({ options: [], total_count: 0 })
      }
      return fetchLookupOptions(sid, query)
    },
    []
  )

  const cancelStream = useCallback(() => {
    abortRef.current?.abort()
    isStreamingRef.current = false
    setIsStreaming(false)
  }, [])

  const resetSession = useCallback(async () => {
    abortRef.current?.abort()
    isStreamingRef.current = false
    setIsStreaming(false)

    setMessages([])
    setSessionId(null)
    sessionIdRef.current = null
    setError(null)
    clearRatings()

    await initSession()
  }, [clearRatings, initSession])

  const loadSession = useCallback(async (id: string) => {
    abortRef.current?.abort()
    isStreamingRef.current = false
    setIsStreaming(false)

    try {
      const detail = await loadSessionData(id, fetchChatSession, restoreSession)
      setSessionId(id)
      sessionIdRef.current = id
      setMessages(convertSessionMessages(detail.messages, compactToolCalls))
      clearRatings()
      setError(null)
    } catch (err) {
      notification.error({ message: 'Failed to load chat session' })
      setError(chatErrorText(err))
    }
  }, [clearRatings, compactToolCalls, fetchChatSession])

  return {
    messages,
    isStreaming,
    error,
    sessionId,
    ratings,
    pendingRatings,
    sendMessage,
    sendDecision,
    sendGroupDecision,
    submitForm,
    submitQuestionSet,
    lookupOptions,
    cancelStream,
    resetSession,
    loadSession,
    rateMessage,
    submitMessageRationale,
  }
}
